import {
  enumPositionDataIngestMode,
  enumPositionGenerationMode,
  kDesiredHolders,
  kPositionBudgetedSalary,
  kPositionCostBandMaximum,
  kPositionCostBandMidpoint,
  kPositionCostBandMinimum,
  SYSTEM_CALCULATE
} from "../constants";

export class PositionConfig {
  constructor(chartObj = {}) {
    this.chart = chartObj;
    this.positionConfig = chartObj.positionChart || {
      enabled: false,
      dataIngestionMode: enumPositionDataIngestMode.single,
      positionGenerationMode: enumPositionGenerationMode.derived,
      synthConfig: {
        autoCreatePositionIDs: true,
        positionIDLookupField: null,
        mergePositions: false,
        mergePositionField: false,
        gapFillConfig: {
          enabled: false,
          autoPurgeEnabled: true,
          subThreshold: 1,
          ageOutDays: 30
        }
      },
      costBandSystem: {},
      useCostBand: false
    };
    this.fields = chartObj.fields;
  }

  isPositionChart() {
    return this.positionConfig.enabled === true;
  }

  isPositionDataIngestSingle() {
    if (this.isPositionChart() === false) {
      return false;
    }
    return this.positionConfig.dataIngestionMode === enumPositionDataIngestMode.single;
  }

  isPositionDataIngestMultiple() {
    if (this.isPositionChart() === false) {
      return false;
    }
    return this.positionConfig.dataIngestionMode === enumPositionDataIngestMode.multi;
  }

  isPositionSynth() {
    if (this.isPositionChart() === false) {
      return false;
    }

    return this.positionConfig.positionGenerationMode === enumPositionGenerationMode.derived; // the OR needs to be removed when we support different data ingest modes across single / multi i ingest modes
  }

  isAutoGeneratePositionIds() {
    if (this.isPositionChart() === false) {
      return false;
    }

    if (this.isPositionSynth() === false) {
      return false;
    }

    const synthConfig = this.positionConfig.synthConfig || {};
    return synthConfig.autoCreatePositionIDs === true;
  }

  isMergePositions() {
    if (this.isPositionChart() === false) {
      return false;
    }

    if (this.isPositionSynth() === false) {
      return false;
    }

    const synthConfig = this.positionConfig.synthConfig || {};
    return synthConfig.mergePositions === true;
  }

  getMergePositionField() {
    if (!this.isMergePositions()) {
      return null;
    }

    const synthConfig = this.positionConfig.synthConfig || {};
    return synthConfig.mergePositionField || null;
  }

  getPositionIdLookupField() {
    if (!this.isPositionSynth()) {
      return null;
    }

    const synthConfig = this.positionConfig.synthConfig || {};
    return synthConfig.positionIDLookupField || null;
  }

  getSynthConfig() {
    return this.positionConfig.synthConfig || {};
  }

  getGapFillConfig() {
    const synthConfig = this.getSynthConfig();
    return (
      synthConfig.gapFillConfig || {
        enabled: false,
        autoPurgeEnabled: true,
        ageOutDays: 30,
        subThreshold: 1
      }
    );
  }

  isGapFillEnabled() {
    if (!this.isPositionSynth()) {
      return false;
    }
    const gapFillConfig = this.getGapFillConfig();
    return gapFillConfig.enabled === true;
  }

  isGapFillAutoPurgeEnabled() {
    const gapFillConfig = this.getGapFillConfig();
    return gapFillConfig.autoPurgeEnabled === true;
  }

  getGapFillAgeOutDays() {
    const gapFillConfig = this.getGapFillConfig();
    return gapFillConfig.ageOutDays || 30;
  }

  hasGapFillNotification() {
    return this.isGapFillConfiguredInAppend();
  }

  isGapFillConfiguredInAppend() {
    if (!this.chart.append || !Array.isArray(this.chart.append)) {
      return false;
    }

    const gapFillTableName = `gap_fill_${this.chart.ID}`;

    return this.chart.append.some(appendEntry => {
      if (appendEntry.override && appendEntry.override.name) {
        return appendEntry.override.name === gapFillTableName;
      }
      return false;
    });
  }

  /**
   * return to format ready for saving in chart or the object itself
   */
  serialize(asChart = false) {
    return asChart ? { positionChart: this.positionConfig } : this.positionConfig;
  }

  /**
   * Cost band related methods
   */

  static SYSTEM_PREFIX = SYSTEM_CALCULATE;

  useCostBand() {
    return this?.positionConfig?.useCostBand || false;
  }

  getCostBandSystemSettings() {
    return this?.positionConfig?.costBandSystem || {};
  }

  isFieldUsingSystemCalculation(fieldKey) {
    const fields = this?.fields || {};
    return fields[fieldKey] === PositionConfig.SYSTEM_PREFIX;
  }

  getSystemCalculationPercentage(fieldKey) {
    if (!this.isFieldUsingSystemCalculation(fieldKey)) return "";

    const systemSettings = this.getCostBandSystemSettings();
    return systemSettings[fieldKey] || this.getDefaultPercentage(fieldKey);
  }

  isCostBandMidpointSystemGenerated() {
    return this.isFieldUsingSystemCalculation("Position Cost Band Midpoint");
  }

  isCostBandMinMaxSystemGenerated() {
    return (
      this.isFieldUsingSystemCalculation("Position Cost Band Minimum") ||
      this.isFieldUsingSystemCalculation("Position Cost Band Maximum")
    );
  }

  getDefaultPercentage(fieldKey) {
    if (fieldKey === "Position Cost Band Minimum") return "75";
    if (fieldKey === "Position Cost Band Maximum") return "125";
    return "100";
  }

  getCostBandFieldType(fieldKey) {
    return this.isFieldUsingSystemCalculation(fieldKey) ? "system" : "field";
  }

  validateCostBandPercentages() {
    if (
      this.isFieldUsingSystemCalculation("Position Cost Band Minimum") &&
      this.isFieldUsingSystemCalculation("Position Cost Band Maximum")
    ) {
      const minPercentage = parseInt(
        this.getSystemCalculationPercentage("Position Cost Band Minimum"),
        10
      );
      const maxPercentage = parseInt(
        this.getSystemCalculationPercentage("Position Cost Band Maximum"),
        10
      );
      return minPercentage <= maxPercentage;
    }
    return true;
  }

  /**
   * System generation methods for other fields
   */

  canSystemGeneratePositionBudgetedSalary() {
    const fields = this?.fields || {};
    const staffingTargetMapped = fields[kDesiredHolders] && fields[kDesiredHolders] !== "";

    const costBandMinMapped =
      fields[kPositionCostBandMinimum] && fields[kPositionCostBandMinimum] !== "";

    const costBandMidMapped =
      fields[kPositionCostBandMidpoint] && fields[kPositionCostBandMidpoint] !== "";

    const costBandMaxMapped =
      fields[kPositionCostBandMaximum] && fields[kPositionCostBandMaximum] !== "";

    return (
      this.useCostBand() &&
      staffingTargetMapped &&
      costBandMaxMapped &&
      costBandMinMapped &&
      costBandMidMapped
    );
  }
}
